/**
 * `createApiPipelinesSource` — the `pipeline-mgmt-api` port over the shared
 * `ApiClient`.
 *
 * The visual builder (`@authe/pipeline-builder`) edits a camelCase
 * {@link PipelineDoc}; `pipeline-mgmt-api` speaks snake_case (`step_name`,
 * `target_tier`, node `ui` hints, …). This adapter crosses that boundary with
 * the dependency-free wire mappers from `@authe/fe-commons/pipeline`
 * (`docToWire` / `docFromWire` / `catalogFromWire` / `issuesFromWire` /
 * `runResultFromWire`), so the wire stays canonically snake_case and the
 * builder stays untouched. Canonical paths (`/pipelines/*`) are mapped to the
 * `private/pipeline-mgmt-api` tier by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { asNullableString, asOptString, asRecord, asString, isRecord, rowsFromWire } from "./wire";
import { walkPages } from "./pagedList";
import {
  catalogFromWire,
  docFromWire,
  docToWire,
  issuesFromWire,
  runResultFromWire,
  templatesFromWire,
  type PipelineDoc,
  type PipelineIssue,
  type PipelineRunResult,
  type PipelineTemplate,
  type StepCatalog,
} from "@authe/fe-commons/pipeline";

/**
 * Tenancy rung a definition is authored at, as `PipelineScope` spells it.
 *
 * No `team`: a rung names the partition a row is written to, and team
 * membership is not a tenancy segment. Sharing a definition with a team is the
 * ABAC carrier `visible_to_team_ids`, which the list route already enforces.
 * The member was here and no reader in the fleet branched on it.
 */
export type PipelineScopeWire = "system" | "customer" | "org" | "tenant" | "user";

/**
 * Which built-in preset a SYSTEM-scope definition materialises. Presets are
 * code-owned (`authe_tx_pipeline.pipeline_presets`) and the backend writes them
 * on every deploy; this is how a row says which one it is, so the list can
 * order and badge presets from DATA instead of matching ids or names. `null` on
 * every authored pipeline.
 */
export interface PipelinePresetRef {
  /** Stable slug: `minimal` / `fast` / `default` / `thorough` (more may land). */
  tier: string;
  /** Sort position, fastest first. */
  rank: number;
  /** One line on what the tier trades away. */
  summary: string;
}

export interface PipelineDefinitionSummary {
  id: string;
  name: string;
  description?: string | null;
  scope: string;
  enabled: boolean;
  isDefault: boolean;
  /** Set on a system preset row; `null` otherwise. */
  preset: PipelinePresetRef | null;
  doc: PipelineDoc;
}

export interface SavePipelineOptions {
  scope?: PipelineScopeWire;
  description?: string | null;
  enabled?: boolean;
  isDefault?: boolean;
  targetChainIds?: string[];
}

export interface PipelinesSource {
  /** Step palette from the live backend registry. */
  catalog(): Promise<StepCatalog>;
  /**
   * Template gallery from the backend SSOT
   * (`authe_tx_pipeline.build_template_catalog`, served at
   * `GET /v0/pipelines/templates`). The builder falls back to its bundled
   * `DEFAULT_TEMPLATES` when this yields nothing.
   */
  templates(): Promise<PipelineTemplate[]>;
  /** Pipeline definitions visible to the caller (system defaults + tenant rows). */
  listDefinitions(): Promise<PipelineDefinitionSummary[]>;
  /** One definition by id (deep-link target; includes its full doc). */
  get(id: string): Promise<PipelineDefinitionSummary | null>;
  /** The system/tenant default pipeline doc, if one is flagged. */
  defaultDoc(): Promise<PipelineDoc | null>;
  /** Persist a doc as a NEW definition (POST); returns the created row id. */
  save(doc: PipelineDoc, opts?: SavePipelineOptions): Promise<{ id: string }>;
  /** Update an EXISTING definition in place (PATCH); returns its id. */
  update(id: string, doc: PipelineDoc, opts?: SavePipelineOptions): Promise<{ id: string }>;
  /** Delete a definition. */
  remove(id: string): Promise<void>;
  /** Authoritative server-side validation against the registry. */
  validate(doc: PipelineDoc): Promise<PipelineIssue[]>;
  /** Run the doc against a sample input without persisting. */
  dryRun(doc: PipelineDoc, input: unknown, options?: DryRunOptions): Promise<PipelineRunResult>;
}

/** Run-time knobs for a dry run (not part of the doc). */
export interface DryRunOptions {
  /** Chain the sample tx is on — the normalize step requires it. */
  chainId?: string;
  /** Ingress provider the sample payload is shaped for (default `moralis`). */
  provider?: string;
}

function definitionFromWire(raw: Record<string, unknown>): PipelineDefinitionSummary {
  return {
    id: asString(raw.id),
    name: asString(raw.name),
    description: asNullableString(raw.description),
    scope: asString(raw.scope, "user"),
    enabled: Boolean(raw.enabled ?? true),
    isDefault: Boolean(raw.is_default ?? raw.isDefault ?? false),
    preset: presetFromWire(raw.preset),
    doc: docFromWire(asRecord(raw.doc)),
  };
}

/**
 * A row's preset marker, or `null`. A malformed marker is treated as absent
 * rather than half-read: a preset with no usable rank would sort as rank 0 and
 * jump to the top of the list, which is worse than showing it as authored.
 */
export function presetFromWire(raw: unknown): PipelinePresetRef | null {
  if (!isRecord(raw)) return null;
  const tier = asString(raw.tier);
  const rank = typeof raw.rank === "number" && Number.isFinite(raw.rank) ? raw.rank : null;
  if (!tier || rank === null) return null;
  return { tier, rank, summary: asString(raw.summary) };
}

export function createApiPipelinesSource(client: ApiClient): PipelinesSource {
  return {
    async catalog(): Promise<StepCatalog> {
      const res = await client.get<unknown>("/pipelines/catalog");
      return catalogFromWire(asRecord(unwrapData(res)));
    },

    async templates(): Promise<PipelineTemplate[]> {
      const res = await client.get<unknown>("/pipelines/templates");
      return templatesFromWire(asRecord(unwrapData(res)));
    },

    async listDefinitions(): Promise<PipelineDefinitionSummary[]> {
      // Paged, because this read always WAS a page and did not say so. The route
      // defaults to 50 and hands back a `next_cursor` that this dropped on the
      // floor, so a tenant with sixty definitions saw fifty — and every caller
      // here is a picker or a name resolver, where a missing pipeline does not
      // read as "there are more" but as "that one does not exist".
      //
      // It also cannot be skipped by asking for a big limit: the route filters
      // for visibility AFTER taking the page, so a page legitimately comes back
      // short WITH a cursor, exactly as the investigations list does.
      //
      // The walk itself lives in `./pagedList` — four sources need it.
      return (await walkPages(client, "/pipelines")).map(definitionFromWire);
    },

    async get(id: string): Promise<PipelineDefinitionSummary | null> {
      const res = await client.get<unknown>(`/pipelines/${encodeURIComponent(id)}`);
      const data = unwrapData(res);
      return isRecord(data) ? definitionFromWire(data) : null;
    },

    async defaultDoc(): Promise<PipelineDoc | null> {
      const definitions = await this.listDefinitions();
      const preferred = definitions.find((d) => d.isDefault) ?? definitions[0];
      return preferred ? preferred.doc : null;
    },

    async save(doc: PipelineDoc, opts: SavePipelineOptions = {}): Promise<{ id: string }> {
      const body = {
        name: doc.name,
        description: opts.description ?? doc.description ?? null,
        scope: opts.scope ?? "user",
        enabled: opts.enabled ?? true,
        is_default: opts.isDefault ?? false,
        target_chain_ids: opts.targetChainIds ?? [],
        doc: docToWire(doc),
      };
      const created = asRecord(unwrapData(await client.post<unknown>("/pipelines", body)));
      // Drop the 30s GET cache so the list reflects the new definition.
      // `invalidateMatching`: the list is read through `walkPages`, which always
      // sends `?limit=100`, and the exact-URL form keys on the resolved URL with
      // its query string — so naming the bare path matched no cache entry and
      // the refetch was served the pre-write page.
      client.invalidateMatching("/pipelines");
      return { id: asOptString(created.id) ?? doc.id };
    },

    async update(id: string, doc: PipelineDoc, opts: SavePipelineOptions = {}): Promise<{ id: string }> {
      // PATCH only the keys we send; `scope` is immutable on update server-side.
      const body: Record<string, unknown> = { name: doc.name, doc: docToWire(doc) };
      if (opts.description !== undefined) body.description = opts.description;
      else if (doc.description !== undefined) body.description = doc.description ?? null;
      if (opts.enabled !== undefined) body.enabled = opts.enabled;
      if (opts.isDefault !== undefined) body.is_default = opts.isDefault;
      if (opts.targetChainIds !== undefined) body.target_chain_ids = opts.targetChainIds;
      const updated = asRecord(
        unwrapData(await client.patch<unknown>(`/pipelines/${encodeURIComponent(id)}`, body)),
      );
      client.invalidateMatching("/pipelines");
      return { id: asOptString(updated.id) ?? id };
    },

    async remove(id: string): Promise<void> {
      // `unwrapData`: the client resolves an `{ok:false}` envelope, so a bare
      // `await` here announced "Pipeline deleted" on a refusal and the
      // definition was still there on the next read.
      unwrapData(await client.delete<void>(`/pipelines/${encodeURIComponent(id)}`));
      // Covers the `?limit=100` list walk and `/pipelines/{id}` in one sweep.
      client.invalidateMatching("/pipelines");
    },

    async validate(doc: PipelineDoc): Promise<PipelineIssue[]> {
      const res = await client.post<unknown>("/pipelines/validate", { doc: docToWire(doc) });
      return issuesFromWire(rowsFromWire(unwrapData(res)));
    },

    async dryRun(doc: PipelineDoc, input: unknown, options: DryRunOptions = {}): Promise<PipelineRunResult> {
      const body: Record<string, unknown> = {
        doc: docToWire(doc),
        input: input ?? {},
        target_tier: doc.targetTier,
      };
      // Prefer the explicit dry-run chain, falling back to the doc's config scope.
      const chainId = options.chainId || doc.scope?.chainId || undefined;
      if (chainId) body.chain_id = chainId;
      if (options.provider) body.provider = options.provider;
      const res = await client.post<unknown>("/pipelines/dry-run", body);
      return runResultFromWire(asRecord(unwrapData(res)));
    },
  };
}
